// import { useState } from 'react';
import { useEffect, useState } from 'react';
import {
  Contributor,
  ContributorRole,
  Cradle,
  CreateMusicalReleaseProps,
  Language,
  MusicalRelease,
  MusicStyle,
  UpdateMusicalReleaseProps,
} from '@solo/app-core';
import { toast } from 'react-toastify';
import {
  Flex,
  Text,
  Button,
  Icon,
  InputTextAutocomplete,
  FileUploadComponent,
  ArtistsTableComponent,
} from '@/app/ui';
import { translate } from '@/app/i18n';
import InputWithLabel from '@/app/ui/molecules/InputWithLabel';
import { getCurrentAndPastYears } from '@/app/utils/GetCurrentAndPastYears';
import { useCradle } from '@/app/contexts';
import { useBlocState } from '@/app/hooks';
import { artistContributorRoles, sanitizeUpc } from '@/app/utils/revelatorPayload';

export interface MusicalReleaseDetailsFromProps {
  musicalRelease?: MusicalRelease;
  detailsFieldsMissing?: boolean;
  closeModal?: () => void;
  onChange?: (musicalRelease: CreateMusicalReleaseProps | UpdateMusicalReleaseProps) => void;
  saveDetails?: (musicalRelease: CreateMusicalReleaseProps | UpdateMusicalReleaseProps) => void;
  setNewArtwork: (artwork: File) => void;
}

const MusicalReleaseDetailsForm = (props: MusicalReleaseDetailsFromProps) => {
  const { musicalRelease, detailsFieldsMissing, closeModal, onChange, setNewArtwork, saveDetails } =
    props;
  const { revelatorDataBloc } = useCradle<Cradle>();
  const revelatorDataState = useBlocState(revelatorDataBloc);
  const [musicalReleaseDetails, setMusicalReleaseDetails] = useState<UpdateMusicalReleaseProps>({
    ...musicalRelease,
  });

  const [fieldsMissing, setFieldsMissing] = useState<boolean>(detailsFieldsMissing || false);

  const [otherArtist, setOtherArtist] = useState<string>();
  const [otherArtistRole, setOtherArtistRole] = useState<number>();
  const [roles, setRoles] = useState<ContributorRole[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [musicStyles, setMusicStyles] = useState<MusicStyle[]>([]);

  const [artwork, setArtowork] = useState<File | undefined>();

  const type = translate('WORKS_PAGES.RELEASE_DETAILS');
  const title = translate('WORKS.RELEASE_TITLE');

  const years = getCurrentAndPastYears(4);

  const validateCopyrightFormat = (copyright: string | undefined): boolean => {
    if (!copyright) return false;
    const regex = /^\d{4}\s.+$/;
    return regex.test(copyright);
  };

  // switch (musicalRelease?.type || releaseType) {
  //   case 'ALBUM':
  //     type = translate('WORKS_PAGES.ALBUM_DETAILS');
  //     title = translate('WORKS.ALBUM_TITLE');
  //     break;
  //   case 'SINGLE':
  //     type = translate('WORKS.SINGLE_DETAILS');
  //     title = translate('WORKS.SINGLE_TITLE');
  //     break;
  //   default:
  //     type = translate('WORKS.EP_DETAILS');
  //     title = translate('WORKS.EP_TITLE');
  // }

  function deleteArtist(artist: Contributor) {
    const artistIndex = musicalReleaseDetails.contributors?.findIndex(
      (a) => a.artist.name === artist.artist.name,
    );
    if (artistIndex === undefined || !musicalReleaseDetails.contributors) return;
    let artists: Contributor[] | undefined;
    if (musicalReleaseDetails.contributors.length > 1) {
      artists = musicalReleaseDetails.contributors?.splice(artistIndex - 1, 1);
    }
    setMusicalReleaseDetails({
      ...musicalReleaseDetails,
      contributors: artists,
    });
  }

  function addNewArtist() {
    if (!musicalReleaseDetails || !otherArtist || otherArtistRole === undefined) return;
    let otherArtists = musicalReleaseDetails.contributors;
    const newArt = { artist: { name: otherArtist }, roleId: otherArtistRole };
    if (!otherArtists) {
      otherArtists = [newArt];
    } else {
      otherArtists.push(newArt);
    }
    setMusicalReleaseDetails({ ...musicalReleaseDetails, contributors: otherArtists });
    setOtherArtist(undefined);
  }

  function checkMandatoryFields() {
    return (
      musicalReleaseDetails.name !== '' &&
      musicalReleaseDetails.artistName &&
      musicalReleaseDetails.artistName !== '' &&
      musicalReleaseDetails.languageId !== undefined &&
      musicalReleaseDetails.primaryMusicStyleId !== undefined &&
      musicalReleaseDetails.copyrightP !== '' &&
      musicalReleaseDetails.copyrightC !== '' &&
      validateCopyrightFormat(musicalReleaseDetails.copyrightP) &&
      validateCopyrightFormat(musicalReleaseDetails.copyrightC) &&
      artwork !== undefined
    );
  }

  function validateDetailsForm(): CreateMusicalReleaseProps | UpdateMusicalReleaseProps {
    if (checkMandatoryFields()) {
      const upc = sanitizeUpc(musicalReleaseDetails.upc);
      const detailsData = {
        ...musicalReleaseDetails,
        contributors: musicalReleaseDetails.contributors || [],
        productCopyrightYear: musicalReleaseDetails.copyrightP || years[0].toString(),
        soundCopyrightYear: musicalReleaseDetails.copyrightC || years[0].toString(),
        upc,
        previouslyReleased: Boolean(upc),
      };
      return detailsData;
    }
    throw new Error('All mandatory fields must be filled!');
  }

  async function saveChanges() {
    if (!saveDetails) return;
    try {
      const detailsInfo = await validateDetailsForm();
      saveDetails(detailsInfo);
      if (closeModal) closeModal();
    } catch (error) {
      if (error instanceof Error) toast.error(error.message);
      setFieldsMissing(true);
    }
  }

  useEffect(() => {
    if (onChange) onChange(musicalReleaseDetails);
  }, [musicalReleaseDetails]);

  useEffect(() => {
    if (artwork) setNewArtwork(artwork);
  }, [artwork]);

  useEffect(() => {
    if (detailsFieldsMissing) setFieldsMissing(detailsFieldsMissing);
  }, [detailsFieldsMissing]);

  useEffect(() => {
    if (revelatorDataState.kind === 'LoadedRevelatorDataState') {
      setRoles(revelatorDataState.contributorRoles || []);
      setLanguages(revelatorDataState.languages || []);
      setMusicStyles(revelatorDataState.musicStyles || []);
    }
  }, [revelatorDataState.kind]);

  return (
    <Flex.Container flexDirection="column" gap="4rem" style={{ width: '100%' }}>
      <Flex.Container gap="25px" flexDirection="column">
        <Text.SubtitleBold>{type}</Text.SubtitleBold>
        <Flex.Container gap="16px" style={{ width: '100%' }} alignItems="flex-end">
          <Flex.Item flex={2}>
            <InputWithLabel
              placeholder={title}
              label={`${title}*`}
              value={musicalReleaseDetails.name}
              onChange={(t) => setMusicalReleaseDetails({ ...musicalReleaseDetails, name: t })}
              mandatoryError={fieldsMissing && !musicalReleaseDetails.name}
            />
          </Flex.Item>
          <Flex.Item flex={2}>
            <InputWithLabel
              label={`${translate('WORKS.MAIN_ARTIST')}*`}
              placeholder={translate('WORKS.MAIN_ARTIST')}
              onChange={(name) =>
                setMusicalReleaseDetails({ ...musicalReleaseDetails, artistName: name })
              }
              value={musicalReleaseDetails.artistName}
              mandatoryError={fieldsMissing && !musicalReleaseDetails.artistName}
            />
          </Flex.Item>
          <Flex.Item flex={2}>
            <InputWithLabel
              label={`${translate('WORKS.LABEL_NAME')}`}
              placeholder={translate('WORKS.LABEL_NAME')}
              onChange={(labelName) =>
                setMusicalReleaseDetails({ ...musicalReleaseDetails, labelName })
              }
              value={musicalReleaseDetails.labelName}
            />
          </Flex.Item>
        </Flex.Container>

        <Flex.Container flexDirection="column" gap="25px">
          <Flex.Container gap="16px" style={{ width: '100%' }} alignItems="flex-end">
            <Flex.Item flex={2}>
              <InputWithLabel
                placeholder={translate('WORKS.CONTRIBUTORS')}
                label={translate('WORKS.CONTRIBUTORS')}
                onChange={(name) => setOtherArtist(name)}
                value={otherArtist}
              />
            </Flex.Item>
            <Flex.Item flex={2}>
              <InputTextAutocomplete
                label={translate('TRACKS.ROLE')}
                placeholder={translate('WORKS_PAGES.TYPES_OF_ROLE')}
                items={Array.from(artistContributorRoles(roles), (role: ContributorRole) => ({
                  value: role.id.toString(),
                  label: role.name,
                }))}
                onChange={(role) => setOtherArtistRole(Number(role))}
              />
            </Flex.Item>
            <Flex.Container flex={1}>
              <Button
                $size="md"
                $variant="primary"
                style={{ height: '38px', width: '170px' }}
                onClick={() => addNewArtist()}
              >
                <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
                  {translate('BUTTON.ADD')}
                </Text.SectionSubtitle>
              </Button>
            </Flex.Container>
          </Flex.Container>
          <Flex.Container>
            {musicalReleaseDetails.contributors && musicalReleaseDetails.contributors.length > 0 && (
              <Flex.Item flex={1}>
                <ArtistsTableComponent
                  artists={musicalReleaseDetails.contributors}
                  deleteArtist={(artist) => deleteArtist(artist)}
                  roles={roles}
                />
              </Flex.Item>
            )}
          </Flex.Container>
        </Flex.Container>
        <Flex.Container gap="16px" style={{ width: '100%' }}>
          {/* <Flex.Container flex={1}>
            <InputDateWithLabel
              defaultValue={
                musicalReleaseDetails.releaseDate
                  ? new Date(musicalReleaseDetails.releaseDate)
                  : undefined
              }
              label={`${translate('WORKS.RELEASE_DATE')}*`}
              onChange={(releaseDate) =>
                setMusicalReleaseDetails({
                  ...musicalReleaseDetails,
                  releaseDate: releaseDate.toDateString(),
                })
              }
              mandatoryError={fieldsMissing && !musicalReleaseDetails.releaseDate}
            />
          </Flex.Container> */}
          {/* <Flex.Container flex={1}>
            <TimePickerWithLabel
              defaultValue={
                musicalReleaseDetails.releaseTime
                  ? dayjs(
                      `01/01/2023 ${musicalReleaseDetails.releaseTime}:00`,
                      'DD-MM-YYYY HH:mm:ss',
                    )
                  : undefined
              }
              label={translate('WORKS.RELEASE_TIME')}
              onChange={(time) =>
                setMusicalReleaseDetails({ ...musicalReleaseDetails, releaseTime: time })
              }
              mandatoryError={fieldsMissing && !musicalReleaseDetails.releaseDate}
            />
          </Flex.Container> */}
        </Flex.Container>
        <Flex.Container gap="16px" style={{ width: '100%' }}>
          <Flex.Container flex={1}>
            <InputTextAutocomplete
              label={`${translate('WORKS.LANGUAGE')}*`}
              tooltip
              items={Array.from(languages, (language: Language) => ({
                value: language.id.toString(),
                label: language.name,
              }))}
              defaultOption={
                musicalReleaseDetails.languageId !== undefined
                  ? {
                      value: musicalReleaseDetails.languageId.toString(),
                      label:
                        languages.find((l) => l.id === musicalReleaseDetails.languageId)?.name ||
                        '',
                    }
                  : undefined
              }
              onChange={(language) =>
                setMusicalReleaseDetails({
                  ...musicalReleaseDetails,
                  languageId: languages.find((l) => l.id === Number(language))?.id || undefined,
                })
              }
              mandatoryError={fieldsMissing && musicalReleaseDetails.languageId === undefined}
            />
          </Flex.Container>
          <Flex.Container flex={1}>
            <InputTextAutocomplete
              label={`${translate('WORKS.PRIMARY_GENRE')}*`}
              placeholder={translate('WORKS.CHOOSE_GENRE')}
              defaultOption={
                musicalReleaseDetails.primaryMusicStyleId !== undefined
                  ? {
                      value:
                        musicStyles.find((s) => s.id === musicalReleaseDetails.primaryMusicStyleId)
                          ?.name || '',
                      label:
                        musicStyles.find((s) => s.id === musicalReleaseDetails.primaryMusicStyleId)
                          ?.name || '',
                    }
                  : undefined
              }
              tooltip
              items={Array.from(musicStyles, (style) => ({
                value: style.id.toString(),
                label: style.name,
              }))}
              onChange={(primaryGenre) =>
                setMusicalReleaseDetails({
                  ...musicalReleaseDetails,
                  primaryMusicStyleId:
                    musicStyles.find((s) => s.id === Number(primaryGenre))?.id || undefined,
                })
              }
              mandatoryError={
                fieldsMissing && musicalReleaseDetails.primaryMusicStyleId === undefined
              }
            />
          </Flex.Container>
          {/* <Flex.Container flex={1}>
            <InputTextAutocomplete
              label={translate('WORKS.SECONDARY_GENRE')}
              placeholder={translate('WORKS.CHOOSE_GENRE')}
              defaultOption={
                musicalReleaseDetails?.secondaryGenre
                  ? {
                      value: musicalReleaseDetails.secondaryGenre,
                      label: musicalReleaseDetails.secondaryGenre,
                    }
                  : undefined
              }
              tooltip
              items={Array.from(genres, (genre) => ({ value: genre, label: genre }))}
              onChange={(secondaryGenre) =>
                setMusicalReleaseDetails({
                  ...musicalReleaseDetails,
                  secondaryGenre,
                })
              }
            />
          </Flex.Container> */}
        </Flex.Container>
      </Flex.Container>
      <Flex.Container gap="25px" flexDirection="column">
        <Text.SubtitleBold>{translate('WORKS.ARTWORK')}*</Text.SubtitleBold>
        <FileUploadComponent
          type="image"
          onChange={(artworkFile) => setArtowork(artworkFile)}
          existingFile={musicalReleaseDetails.resourceId}
          subtitle={translate('WORKS.ARTWORK_SUBTITLE')}
          mandatoryError={fieldsMissing && !artwork}
        />
      </Flex.Container>
      <Flex.Container flexDirection="column" gap="15px">
        <Flex.Container alignItems="center" gap="1rem">
          <Text.SubtitleBold>UPC / EAN</Text.SubtitleBold>
          <Icon.InfoIcon />
        </Flex.Container>
        <Text.LabelMedium>{translate('WORKS.UPC_INFO')}</Text.LabelMedium>
        <Flex.Container style={{ width: '50%' }}>
          <InputWithLabel
            type="numeric"
            placeholder="UPC / EAN"
            label="UPC / EAN"
            tooltip
            onChange={(upc) => setMusicalReleaseDetails({ ...musicalReleaseDetails, upc })}
            value={musicalReleaseDetails.upc}
          />
        </Flex.Container>
      </Flex.Container>
      <Flex.Container gap="25px" flexDirection="column">
        <Text.SubtitleBold>Copyright</Text.SubtitleBold>
        <Flex.Container gap="16px" style={{ width: '100%' }}>
          <Flex.Item flex={1}>
            <InputWithLabel
              label={`${translate('WORKS.PRODUCT_COPYRIGHT')}*`}
              placeholder={translate('WORKS.PRODUCT_COPYRIGHT')}
              tooltip
              value={musicalReleaseDetails.copyrightP}
              onChange={(copyrightP) =>
                setMusicalReleaseDetails({ ...musicalReleaseDetails, copyrightP })
              }
              mandatoryError={
                fieldsMissing &&
                (!musicalReleaseDetails.copyrightP ||
                  musicalReleaseDetails.copyrightP === '' ||
                  !validateCopyrightFormat(musicalReleaseDetails.copyrightP))
              }
              errorMessage={
                fieldsMissing &&
                musicalReleaseDetails.copyrightP &&
                !validateCopyrightFormat(musicalReleaseDetails.copyrightP)
                  ? translate('WORKS.COPYRIGHT_FORMAT_ERROR')
                  : undefined
              }
            />
          </Flex.Item>
          <Flex.Item flex={1}>
            <InputWithLabel
              value={musicalReleaseDetails.copyrightC}
              label={`${translate('WORKS.SOUND_RECORDING_COPYRIGHT')}*`}
              placeholder={translate('WORKS.SOUND_RECORDING_COPYRIGHT')}
              tooltip
              onChange={(copyrightC) =>
                setMusicalReleaseDetails({ ...musicalReleaseDetails, copyrightC })
              }
              mandatoryError={
                fieldsMissing &&
                (!musicalReleaseDetails.copyrightC ||
                  musicalReleaseDetails.copyrightC === '' ||
                  !validateCopyrightFormat(musicalReleaseDetails.copyrightC))
              }
              errorMessage={
                fieldsMissing &&
                musicalReleaseDetails.copyrightC &&
                !validateCopyrightFormat(musicalReleaseDetails.copyrightC)
                  ? translate('WORKS.COPYRIGHT_FORMAT_ERROR')
                  : undefined
              }
            />
          </Flex.Item>
        </Flex.Container>
      </Flex.Container>
      {closeModal && musicalRelease && (
        <Flex.Container style={{ marginLeft: 'auto' }} gap="20px">
          <Button
            $transparent
            $variant="primary"
            onClick={() => {
              closeModal();
            }}
          >
            <Text.SectionSubtitle>{translate('PROFILE_INFO.CANCEL')}</Text.SectionSubtitle>
          </Button>

          <Button
            $size="md"
            $variant="primary"
            style={{ width: '290px' }}
            onClick={() => {
              saveChanges();
            }}
          >
            <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
              {translate('PROFILE_INFO.SAVE')}
            </Text.SectionSubtitle>
          </Button>
        </Flex.Container>
      )}
    </Flex.Container>
  );
};

export default MusicalReleaseDetailsForm;
